import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface AuthError {
  message: string;
}

export function CallbackPage() {
  const navigate = useNavigate();

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get session from URL
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;

      if (session?.user) {
        // Check if user exists in profiles
        const { data: profile } = await supabase
          .from('profiles')
          .select('role, admin_role')
          .eq('id', session.user.id)
          .single();

        // If no profile exists, create one with appropriate role
        if (!profile) {
          const isGithubLogin = session.user.app_metadata.provider === 'github';
          const adminRole = isGithubLogin ? 'founder' : 'admin';

          await supabase.from('profiles').insert({
            id: session.user.id,
            email: session.user.email,
            role: 'admin',
            admin_role: adminRole,
          });

          // For GitHub users, also grant all permissions
          if (isGithubLogin) {
            await supabase.from('user_permissions').insert({
              user_id: session.user.id,
              permissions: {
                manageUsers: true,
                manageSubscriptions: true,
                manageSSO: true,
                viewAnalytics: true,
                viewHealth: true,
                manageSystem: true,
                manageAdmins: true,
                editPermissions: true,
                configureBot: true,
                editRoles: true
              }
            });
          } else {
            // For regular login, grant basic admin permissions
            await supabase.from('user_permissions').insert({
              user_id: session.user.id,
              permissions: {
                manageUsers: true,
                manageSubscriptions: true,
                manageSSO: true,
                viewAnalytics: true,
                viewHealth: true,
                manageSystem: false,
                manageAdmins: false,
                editPermissions: false,
                configureBot: false,
                editRoles: false
              }
            });
          }

          navigate('/dashboard');
          return;
        }

        // Redirect based on role
        if (profile.role === 'admin') {
          navigate('/admin/customers');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/login');
      }
    } catch (error: unknown) {
      const authError = error as AuthError;
      console.error('Auth callback error:', authError.message);
      toast.error('Authentication failed');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-lg">
        Completing sign in...
      </div>
    </div>
  );
}